import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Upgrade a Quick (anonymous) account to a Full account.
 * Supabase preserves user.id when linking an email or OAuth identity,
 * so all existing contributions remain attached.
 */
export function UpgradeAccountSheet({ open, onOpenChange, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upgradeWithEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ email, password });
      if (error) throw error;
      await supabase
        .from('profiles')
        .update({ is_anonymous: false, upgraded_at: new Date().toISOString(), email })
        .eq('id', (await supabase.auth.getUser()).data.user!.id);
      toast.success('Имэйл нэмэгдлээ. Баталгаажуулах холбоосыг шалгана уу.');
      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message ?? 'Бүртгэл хадгалахад алдаа гарлаа.');
    } finally {
      setLoading(false);
    }
  };

  const linkGoogle = async () => {
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.linkIdentity({
        provider: 'google',
        options: { redirectTo: window.location.origin + '/auth/callback' },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err?.message ?? 'Google холбоход алдаа гарлаа.');
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto sm:max-w-md sm:mx-auto">
        <SheetHeader>
          <SheetTitle>Бүртгэлээ хадгалах уу?</SheetTitle>
          <SheetDescription>
            Имэйл эсвэл Google нэмбэл санал хураалт, оруулсан мэдээлэл, тайлбарууд тань хэвээр үлдэх ба ямар ч төхөөрөмжөөс нэвтрэх боломжтой болно.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={upgradeWithEmail} className="mt-4 space-y-3">
          <div>
            <Label htmlFor="up-email">Имэйл</Label>
            <Input id="up-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="up-password">Шинэ нууц үг</Label>
            <Input id="up-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Имэйл нэмэх
          </Button>
        </form>

        <div className="my-4 text-center text-xs text-muted-foreground">эсвэл</div>

        <Button variant="outline" className="w-full" onClick={linkGoogle} disabled={loading}>
          Google-ээр холбох
        </Button>

        <Button variant="ghost" className="w-full mt-2" onClick={() => onOpenChange(false)} disabled={loading}>
          Дараа
        </Button>
      </SheetContent>
    </Sheet>
  );
}